/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 29th 2024, 10:41:41 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Typography from "@mui/material/Typography";
import { formatBytes } from "lib/utils";
import { OSAPIForm, OSFileInput } from "ui-widgets";
import { UseAPIFormReturn } from "lib/api";

type LibraryAssetUploadFormProps<T> = {
  useAPIFormReturn: UseAPIFormReturn<T>;
  uploadedFiles: File[];
  showSubmitButton?: boolean;
  onAddFiles: (files: File[]) => void;
} & Partial<Omit<React.ComponentProps<typeof OSFileInput>, "filePreviewProps">> & {
    filePreviewProps: Partial<React.ComponentProps<typeof OSFileInput>["filePreviewProps"]>;
  };

export const LibraryAssetUploadForm = <T,>({
  useAPIFormReturn,
  uploadedFiles,
  onAddFiles,
  showSubmitButton = true,
  ...rest
}: LibraryAssetUploadFormProps<T>) => {
  const { filePreviewProps, ...fileInputProps } = rest;
  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} showSubmitButton={showSubmitButton}>
      <OSFileInput
        dropTargetText="Drag files from your local machine and drop them here to upload"
        filePreviewProps={{
          validFileMessage: (
            <>
              {uploadedFiles?.map((uploadedFile) => (
                <>
                  <p>{uploadedFile?.name}</p>
                  <Typography variant="subtitle1">
                    {formatBytes(uploadedFile?.size)} | {uploadedFile?.type}
                  </Typography>
                </>
              ))}
            </>
          ),
          invalidFileMessage: (
            <>
              {uploadedFiles?.map((uploadedFile) => (
                <p>{uploadedFile?.name} is not valid.</p>
              ))}
            </>
          ),
          ...filePreviewProps,
        }}
        isValid={uploadedFiles?.length > 0}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={onAddFiles}
        {...fileInputProps}
      />
    </OSAPIForm>
  );
};
